import { arithmeticGeometricMean, DEG, formatNumber, rk4, TAU } from '../math.js';

function energyOf(state, params) {
  const { length: L, mass: m, gravity: g } = params;
  const kinetic = 0.5 * m * L * L * state.omega * state.omega;
  const potential = m * g * L * (1 - Math.cos(state.theta));
  return { kinetic, potential, total: kinetic + potential };
}

export const pendulum = {
  id: 'pendulum',
  order: 1,
  meta: {
    title: '单摆实验',
    shortTitle: '单摆',
    eyebrow: 'MECHANICAL OSCILLATION',
    tagline: '让重力写下时间的节拍',
    description:
      '改变摆长、重力与初始摆角，观察摆球的往复运动、能量转化和周期变化。用同一套参数生成配套练习与实验指导书。',
    accent: '#5eead4',
    accentRgb: '94, 234, 212',
    grade: '高中物理 · 机械振动',
    duration: '建议 15–20 分钟',
    objectives: [
      '验证小角度下单摆周期公式 T = 2π√(L/g)，理解周期与摆长、重力加速度的关系。',
      '观察摆球在最高点与最低点之间的动能、势能转化，理解机械能守恒条件。',
      '比较大角度周期与小角度近似周期的差异，认识简谐近似的适用范围。',
      '学会利用多次全振动计时降低测量误差，并用图像法处理实验数据。',
    ],
    apparatus: ['细线', '金属摆球', '铁架台', '量角器', '米尺', '秒表', 'PhysLab 仿真终端'],
    theory: [
      '设摆长为 $L$，摆角为 $\\theta$。摆球受重力 $mg$ 与细线拉力 $T$，沿圆弧切线方向的回复力为 $F=-mg\\sin\\theta$。',
      '当 $\\theta$ 很小时，$\\sin\\theta\\approx\\theta$，运动方程化为 $\\ddot\\theta+\\dfrac{g}{L}\\theta=0$，属于简谐振动，周期为 $T_0=2\\pi\\sqrt{L/g}$。',
      '不采用小角度近似时，精确周期为 $T=\\dfrac{2\\pi\\sqrt{L/g}}{\\mathrm{AGM}(1,\\cos(\\theta_0/2))}$，其中 $\\theta_0$ 为最大摆角。摆角越大，周期越长。',
      '取最低点为势能零点，机械能 $E=\\dfrac12 mL^2\\omega^2+mgL(1-\\cos\\theta)$。无阻尼时 $E$ 守恒；有阻尼时机械能转化为内能。',
      '最低点绳张力 $T=mg+\\dfrac{mv^2}{L}=mg(3-2\\cos\\theta_0)$；最高点速度为 0，绳张力 $T=mg\\cos\\theta_0$。',
    ],
    procedure: [
      '将摆长设为目标值，用米尺测量悬点到摆球球心的距离并记录。',
      '把摆球拉离平衡位置至设定摆角（建议不超过 10°），由静止释放，确认摆球在竖直平面内摆动。',
      '用计时器记录 30 次全振动的总时间，重复 3 次取平均，计算周期 $T=t/n$。',
      '依次改变摆长（如 0.4、0.6、0.8、1.0 m），重复上述测量。',
      '绘制 $T^2-L$ 图像，若为过原点直线，则斜率 $k=4\\pi^2/g$，由此求出 $g$。',
      '在仿真中开启“大角度”对照，比较实际周期与小角度公式的差异。',
    ],
    safety: [
      '摆角不宜过大，避免摆球与铁架台碰撞。',
      '释放摆球时不要给它初速度，保证初始条件一致。',
      '使用秒表时以摆球经过平衡位置开始计时，以减小反应时间误差。',
    ],
    formulas: [
      { label: '小角度周期', latex: 'T_0=2\\pi\\sqrt{L/g}', note: 'θ₀ ≲ 10° 时误差较小' },
      { label: '精确周期', latex: 'T=\\dfrac{T_0}{\\mathrm{AGM}(1,\\cos(\\theta_0/2))}', note: '大角度修正' },
      { label: '机械能', latex: 'E=\\tfrac12 mL^2\\omega^2+mgL(1-\\cos\\theta)', note: '以最低点为势能零点' },
      { label: '最低点张力', latex: 'T=mg(3-2\\cos\\theta_0)', note: '无阻尼、由静止释放' },
    ],
    recordTable: {
      title: '摆长—周期测量记录表',
      columns: ['次数', '摆长 L / m', '30 次全振动时间 t / s', '周期 T = t/30 / s', 'T² / s²'],
      rows: 5,
    },
  },
  schema: [
    {
      key: 'length',
      label: '摆长',
      symbol: 'L',
      unit: 'm',
      min: 0.2,
      max: 3,
      step: 0.05,
      default: 1,
      precision: 2,
      group: '几何参数',
      apply: 'reset',
      description: '悬点到摆球球心的距离。摆长越长，周期越大。',
    },
    {
      key: 'mass',
      label: '摆球质量',
      symbol: 'm',
      unit: 'kg',
      min: 0.1,
      max: 5,
      step: 0.05,
      default: 1,
      precision: 2,
      group: '几何参数',
      apply: 'live',
      description: '在小角度简谐近似下，周期与质量无关；质量影响能量和绳张力。',
    },
    {
      key: 'gravity',
      label: '重力加速度',
      symbol: 'g',
      unit: 'm/s²',
      min: 1.6,
      max: 20,
      step: 0.1,
      default: 9.8,
      precision: 1,
      group: '环境参数',
      apply: 'live',
      description: '可切换月球（1.6）、火星（3.7）或地球（9.8）等重力环境。',
    },
    {
      key: 'initialAngle',
      label: '初始摆角',
      symbol: 'θ₀',
      unit: '°',
      min: 1,
      max: 80,
      step: 1,
      default: 30,
      precision: 0,
      group: '初始条件',
      apply: 'reset',
      description: '摆球由静止释放时的最大摆角。超过约 15° 后，小角度周期公式的误差开始明显。',
    },
    {
      key: 'damping',
      label: '阻尼系数',
      symbol: 'b',
      unit: '1/s',
      min: 0,
      max: 0.8,
      step: 0.01,
      default: 0,
      precision: 2,
      group: '环境参数',
      apply: 'live',
      description: '空气阻力等耗散作用的等效系数。为 0 时机械能守恒。',
    },
  ],
  createState(params) {
    return {
      t: 0,
      theta: params.initialAngle * DEG,
      omega: 0,
    };
  },
  advance(state, params, dt) {
    const { length: L, gravity: g, damping: b } = params;
    const derivative = ([theta, omega]) => [omega, -(g / L) * Math.sin(theta) - b * omega];
    const [theta, omega] = rk4([state.theta, state.omega], dt, derivative);
    return { t: state.t + dt, theta, omega };
  },
  measure(state, params) {
    const { length: L, mass: m, gravity: g, initialAngle, damping } = params;
    const energy = energyOf(state, params);
    const speed = Math.abs(state.omega) * L;
    const smallPeriod = TAU * Math.sqrt(L / g);
    const largePeriod = smallPeriod / arithmeticGeometricMean(1, Math.cos((initialAngle * DEG) / 2));
    const tension = m * (g * Math.cos(state.theta) + L * state.omega * state.omega);
    const correction = (largePeriod / smallPeriod - 1) * 100;
    const status = damping > 0
      ? { tone: 'info', label: '阻尼振动', detail: '机械能随摆动逐渐转化为内能' }
      : { tone: 'ok', label: '理想单摆', detail: '忽略阻力，机械能守恒' };

    return {
      time: state.t,
      energy,
      momentum: null,
      status,
      readouts: [
        { key: 'angle', label: '摆角', value: state.theta / DEG, unit: '°', precision: 1 },
        { key: 'omega', label: '角速度', value: state.omega, unit: 'rad/s', precision: 2 },
        { key: 'speed', label: '线速度', value: speed, unit: 'm/s', precision: 2 },
        { key: 'tension', label: '绳张力', value: tension, unit: 'N', precision: 2 },
        { key: 'period', label: '小角度周期', value: smallPeriod, unit: 's', precision: 3 },
        {
          key: 'largePeriod',
          label: '大角度周期',
          value: largePeriod,
          unit: 's',
          precision: 3,
          hint: `比小角度公式长 ${formatNumber(correction, 1)}%`,
        },
      ],
      extras: {
        smallPeriod,
        largePeriod,
        periodCorrectionPct: correction,
        tension,
        speed,
        maxSpeed: Math.sqrt(2 * g * L * (1 - Math.cos(initialAngle * DEG))),
      },
    };
  },
  reference(state, params) {
    return { energy: energyOf(state, params).total, momentum: null };
  },
  conservation(params) {
    return { energy: params.damping > 0 ? 'dissipative' : 'conserved', momentum: null };
  },
  isFinished() {
    return false;
  },
  describe(params) {
    const small = TAU * Math.sqrt(params.length / params.gravity);
    const large = small / arithmeticGeometricMean(1, Math.cos((params.initialAngle * DEG) / 2));
    return `小角度周期 ${formatNumber(small, 3)} s；当前摆角下精确周期 ${formatNumber(large, 3)} s（AGM 修正）`;
  },
};
